import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';

interface ProgressProps {
  value: number;
  max?: number;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'error';
  showLabel?: boolean;
  showPercentage?: boolean;
  animated?: boolean;
  gradient?: boolean;
  className?: string;
  label?: string;
}

export const Progress: React.FC<ProgressProps> = ({
  value,
  max = 100,
  size = 'md',
  variant = 'primary',
  showLabel = false,
  showPercentage = false,
  animated = true,
  gradient = false,
  className = '',
  label,
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  const sizeClasses = {
    sm: "h-1.5",
    md: "h-2.5",
    lg: "h-4",
  };

  const bgClasses = "bg-neutral-200 rounded-full overflow-hidden";

  const variantClasses = gradient ? {
    default: "bg-gradient-to-r from-neutral-400 to-neutral-600",
    primary: "bg-gradient-to-r from-primary-400 to-primary-600",
    success: "bg-gradient-to-r from-success-400 to-success-600",
    warning: "bg-gradient-to-r from-warning-400 to-warning-600",
    error: "bg-gradient-to-r from-error-400 to-error-600",
  } : {
    default: "bg-neutral-500",
    primary: "bg-primary-600",
    success: "bg-success-600",
    warning: "bg-warning-500",
    error: "bg-error-600",
  };

  return (
    <div className={cn("w-full", className)}>
      {(showLabel || label) && (
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-neutral-700">
            {label || "Progress"}
          </span>
          {showPercentage && (
            <span className="text-sm text-neutral-600">
              {Math.round(percentage)}%
            </span>
          )}
        </div>
      )}

      <div className={cn(bgClasses, sizeClasses[size])}>
        <motion.div
          className={cn(
            "h-full rounded-full transition-all duration-500 ease-out",
            variantClasses[variant]
          )}
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{
            duration: animated ? 0.8 : 0,
            ease: [0.16, 1, 0.3, 1]
          }}
        />
      </div>
    </div>
  );
};

export const CircularProgress: React.FC<{
  value: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  variant?: 'primary' | 'success' | 'warning' | 'error';
  showPercentage?: boolean;
  animated?: boolean;
  className?: string;
}> = ({
  value,
  max = 100,
  size = 80,
  strokeWidth = 6,
  variant = 'primary',
  showPercentage = true,
  animated = true,
  className = '',
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const colorClasses = {
    primary: "text-primary-600",
    success: "text-success-600",
    warning: "text-warning-500",
    error: "text-error-600",
  };

  return (
    <div className={cn("relative inline-flex items-center justify-center", className)}>
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          className="text-neutral-200"
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeLinecap="round"
          className={colorClasses[variant]}
          style={{
            strokeDasharray: circumference,
          }}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: animated ? strokeDashoffset : circumference - (percentage / 100) * circumference }}
          transition={{ duration: animated ? 1 : 0, ease: "easeOut" }}
        />
      </svg>
      {showPercentage && (
        <span className={cn(
          "absolute inset-0 flex items-center justify-center text-sm font-semibold",
          colorClasses[variant]
        )}>
          {Math.round(percentage)}%
        </span>
      )}
    </div>
  );
};

export const MultiProgress: React.FC<{
  segments: Array<{
    value: number;
    variant: 'primary' | 'success' | 'warning' | 'error';
    label?: string;
  }>;
  max?: number;
  size?: 'sm' | 'md' | 'lg';
  showLabels?: boolean;
  className?: string;
}> = ({
  segments,
  max = 100,
  size = 'md',
  showLabels = false,
  className = '',
}) => {
  const sizeClasses = {
    sm: "h-1.5",
    md: "h-2.5",
    lg: "h-4",
  };

  const variantClasses = {
    primary: "bg-primary-600",
    success: "bg-success-600",
    warning: "bg-warning-500",
    error: "bg-error-600",
  };

  const totalValue = segments.reduce((sum, segment) => sum + segment.value, 0);

  return (
    <div className={cn("w-full", className)}>
      {showLabels && (
        <div className="flex flex-wrap gap-2 mb-2">
          {segments.map((segment, index) => (
            <div key={index} className="flex items-center text-sm">
              <div className={cn("w-3 h-3 rounded mr-1", variantClasses[segment.variant])} />
              <span className="text-neutral-700">
                {segment.label} ({Math.round((segment.value / totalValue) * 100)}%)
              </span>
            </div>
          ))}
        </div>
      )}

      <div className={cn("bg-neutral-200 rounded-full overflow-hidden flex", sizeClasses[size])}>
        {segments.map((segment, index) => {
          const percentage = (segment.value / max) * 100;
          return (
            <motion.div
              key={index}
              className={variantClasses[segment.variant]}
              initial={{ width: 0 }}
              animate={{ width: `${percentage}%` }}
              transition={{
                duration: 0.8,
                delay: index * 0.1,
                ease: [0.16, 1, 0.3, 1]
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

export default Progress;